import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  marketplaceDNAEngineApi,
  type DNABenchmark,
  type DNADiff,
  type DNADomain,
  type DNAScore,
  type DNASnapshot,
  type DNASummary,
  type DNATimelineEvent,
} from '../../api/marketplace-dna-engine';

type Theme = 'light' | 'dark';
type Tab = 'overview' | 'domains' | 'benchmarks' | 'snapshots' | 'timeline' | 'compare';

const DEFAULT_ASIN = 'B0CX123456';

const priorityCls = (p?: string) =>
  p === 'critical' ? 'text-red-600' : p === 'high' ? 'text-orange-600' : p === 'medium' ? 'text-amber-600' : 'text-slate-500';

function Skeleton({ className = 'h-20' }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800 ${className}`} />;
}

export function MarketplaceDNAEnginePage() {
  const { asin: routeAsin } = useParams<{ asin: string }>();
  const asin = routeAsin ?? DEFAULT_ASIN;

  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [summary, setSummary] = useState<DNASummary | null>(null);
  const [domains, setDomains] = useState<DNADomain[]>([]);
  const [scores, setScores] = useState<DNAScore[]>([]);
  const [benchmarks, setBenchmarks] = useState<DNABenchmark[]>([]);
  const [snapshots, setSnapshots] = useState<DNASnapshot[]>([]);
  const [timeline, setTimeline] = useState<DNATimelineEvent[]>([]);
  const [diff, setDiff] = useState<DNADiff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = dark ? 'border-teal-900/40 bg-zinc-900' : 'border-teal-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-teal-50/20 text-slate-900';
  const accent = dark ? 'text-teal-300' : 'text-teal-700';
  const tabActive = 'bg-teal-600 text-white';

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [sum, detail, sc, bench, snaps, tl, cmp] = await Promise.all([
        marketplaceDNAEngineApi.summary(asin),
        marketplaceDNAEngineApi.detail(asin).catch(() => null),
        marketplaceDNAEngineApi.scores(asin),
        marketplaceDNAEngineApi.benchmarks(asin),
        marketplaceDNAEngineApi.snapshots(asin),
        marketplaceDNAEngineApi.timeline(asin),
        marketplaceDNAEngineApi.compare(asin, 10, 12).catch(() => null),
      ]);
      setSummary(sum);
      setDomains(detail?.domains ?? []);
      setScores(sc);
      setBenchmarks(bench);
      setSnapshots(snaps);
      setTimeline(tl);
      setDiff(cmp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Marketplace DNA');
    } finally {
      setLoading(false);
    }
  }, [asin]);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'domains', label: 'DNA Domains' },
    { id: 'benchmarks', label: 'Benchmarks' },
    { id: 'snapshots', label: 'Snapshots' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'compare', label: 'Compare' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main" aria-label={`Marketplace DNA ${asin}`}>
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000105 · Marketplace DNA Engine · Digital Product Twin</p>
            <h1 className="text-xl font-semibold">Marketplace DNA — {asin}</h1>
            {summary && (
              <div className={`mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm ${muted}`}>
                <span>Version {summary.version}</span>
                <span>{summary.metadata.brand}</span>
                <span>{summary.metadata.category}</span>
                <span>Confidence {(summary.confidence * 100).toFixed(0)}%</span>
                <span>Freshness: {summary.metadata.data_freshness}</span>
                <span>{summary.metadata.snapshot_count} snapshots</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/products/${asin}/workspace`} className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>ASIN 360</Link>
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
          </div>
        </div>
        {!loading && summary && (
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div className={`rounded-xl border px-6 py-3 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>Overall DNA Score</p>
              <p className="text-3xl font-bold">{summary.overall_score}</p>
            </div>
            {summary.top_domains.slice(0, 3).map((s) => (
              <ScorePill key={s.key} score={s} card={card} muted={muted} accent={accent} />
            ))}
          </div>
        )}
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
          {loading ? Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="h-16" />)
            : scores.map((s) => <ScoreCard key={s.key} score={s} card={card} muted={muted} priorityCls={priorityCls} />)}
        </div>
      )}

      {tab === 'domains' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? <Skeleton /> : domains.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold">{d.name}</p>
                <span className={`text-lg font-bold ${accent}`}>{d.score}</span>
              </div>
              <p className={`mt-1 text-xs ${muted}`}>v{d.version} · {d.freshness} · {d.status}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'benchmarks' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : benchmarks.map((b) => (
            <div key={b.domain + b.group} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold capitalize">{b.domain.replace('_', ' ')}</p>
                  <p className={`text-xs ${muted}`}>vs {b.group.replace('_', ' ')} · {b.percentile}th percentile</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${b.gap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{b.gap >= 0 ? '+' : ''}{b.gap} gap</p>
                  <p className={`text-xs ${muted}`}>Score {b.score} / Benchmark {b.benchmark}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'snapshots' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : snapshots.map((s) => (
            <div key={s.id} className={`flex flex-wrap items-center justify-between rounded-xl border p-3 ${card}`}>
              <div>
                <p className="font-medium capitalize">{s.type.replace('_', ' ')} snapshot</p>
                <p className={`text-xs ${muted}`}>Version {s.version} · {new Date(s.created_at).toLocaleString()}</p>
              </div>
              <span className={`font-semibold ${accent}`}>Score {s.overall_score}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'timeline' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          {loading ? <Skeleton /> : timeline.map((e) => (
            <div key={e.id} className="flex gap-3 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800">
              <time className={`shrink-0 text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</time>
              <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] uppercase dark:bg-teal-900">{e.type}</span>
              <span className="text-sm">{e.title}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'compare' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          {loading ? <Skeleton /> : diff ? (
            <>
              <p className="font-semibold">Version {diff.from_version} → {diff.to_version}</p>
              <ul className={`mt-3 space-y-2 text-sm ${muted}`}>
                {diff.changes.map((c) => (
                  <li key={c.domain + c.field}>
                    <strong className={accent}>{c.domain}</strong> · {c.field}: {c.old_value} → {c.new_value}
                    <span className="ml-2 rounded bg-slate-100 px-1 text-xs dark:bg-slate-800">{c.change_type}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <p className={`text-sm ${muted}`}>No comparison available.</p>}
        </div>
      )}

      <p className={`text-xs ${muted}`}>DNA evolves automatically — never manually edited. Every recommendation originates from Marketplace DNA.</p>
    </div>
  );
}

function ScorePill({ score, card, muted, accent }: { score: DNAScore; card: string; muted: string; accent: string }) {
  return (
    <div className={`rounded-lg border px-4 py-2 ${card}`}>
      <p className={`text-[10px] uppercase ${muted}`}>{score.name}</p>
      <p className={`font-semibold ${accent}`}>{score.current}{score.trend && <span className="ml-1 text-xs">{score.trend}</span>}</p>
    </div>
  );
}

function ScoreCard({ score, card, muted, priorityCls }: { score: DNAScore; card: string; muted: string; priorityCls: (p?: string) => string }) {
  return (
    <div className={`rounded-lg border p-3 ${card}`}>
      <p className={`truncate text-[10px] uppercase ${muted}`}>{score.name}</p>
      <p className="text-lg font-semibold">{score.current}</p>
      <div className={`flex flex-wrap gap-x-2 text-[10px] ${muted}`}>
        {score.benchmark != null && <span>Bench {score.benchmark}</span>}
        {score.gap != null && <span className={score.gap >= 0 ? 'text-emerald-600' : 'text-red-600'}>Gap {score.gap >= 0 ? '+' : ''}{score.gap}</span>}
        {score.priority && <span className={priorityCls(score.priority)}>{score.priority}</span>}
      </div>
    </div>
  );
}
